import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Activity, ArrowRight, BrainCircuit, Clock3, HeartPulse, NotebookPen,
  Pill, Radar, ShieldAlert, Stethoscope,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { usePatients } from "@/lib/usePatients";
import { readPatientVitals } from "@/lib/clinical/vitalPersistence";
import { PRIMARY_PATIENT_ID, CLINICAL_WINDOW_START, CLINICAL_WINDOW_END } from "@/lib/clinical/singlePatientMode";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { QuickIngestionWizard } from "@/components/clinical/QuickIngestionWizard";
import { useState } from "react";

function Metric({ label, value, unit, sub, icon: Icon }: any) {
  return (
    <Card className="clinical-metric">
      <div className="flex items-start justify-between gap-3">
        <div className="clinical-kicker">{label}</div>
        <Icon className="h-4 w-4 text-primary" />
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className="clinical-number">{value ?? "—"}</span>
        {unit ? <span className="text-xs text-muted-foreground">{unit}</span> : null}
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>
    </Card>
  );
}

function time(value?: string | null) {
  if (!value) return "sem horário";
  return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

export default function Dashboard() {
  const { data: patients = [] } = usePatients();
  const patient: any = patients[0];
  const [doseOpen, setDoseOpen] = useState(false);

  const { data: doses = [] } = useQuery({
    queryKey: ["command-center-doses", PRIMARY_PATIENT_ID],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("medication_dose_logs")
        .select("*")
        .eq("patient_id", PRIMARY_PATIENT_ID)
        .is("deleted_at", null)
        .gte("created_at", CLINICAL_WINDOW_START)
        .lt("created_at", CLINICAL_WINDOW_END)
        .order("actual_time", { ascending: false })
        .limit(240);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: vitals = [] } = useQuery({
    queryKey: ["command-center-vitals", PRIMARY_PATIENT_ID],
    queryFn: async () => (await readPatientVitals(PRIMARY_PATIENT_ID))
      .filter((row: any) => {
        const t = new Date(row.created_at ?? row.measured_at).getTime();
        return t >= new Date(CLINICAL_WINDOW_START).getTime() && t < new Date(CLINICAL_WINDOW_END).getTime();
      }),
  });

  const { data: checkins = [] } = useQuery({
    queryKey: ["command-center-checkins", PRIMARY_PATIENT_ID],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("session_checkins")
        .select("*")
        .eq("patient_id", PRIMARY_PATIENT_ID)
        .is("deleted_at", null)
        .gte("created_at", CLINICAL_WINDOW_START)
        .lt("created_at", CLINICAL_WINDOW_END)
        .order("checkin_at", { ascending: false })
        .limit(80);
      if (error) throw error;
      return data ?? [];
    },
  });

  const latestVital: any = vitals[0];
  const latestCheckin: any = checkins[0];
  const recent = useMemo(() => doses.slice(0, 6), [doses]);
  const uniqueSubstances = useMemo(() => new Set(doses.map((d: any) => d.substance_name).filter(Boolean)).size, [doses]);

  const windowStartLabel = new Date(CLINICAL_WINDOW_START).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
  const windowEndDate = new Date(new Date(CLINICAL_WINDOW_END).getTime() - 1);
  const windowEndLabel = windowEndDate.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <div className="clinical-command space-y-5 md:space-y-6">
      <section className="clinical-hero">
        <div className="relative z-10 min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="clinical-kicker">PSICONORTE · CLINICAL WORKSPACE</span>
            <Badge variant="outline" className="rounded-full text-[10px]">PAINEL PRINCIPAL</Badge>
          </div>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">Painel clínico</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{patient?.social_name || patient?.full_name || "Paciente principal"}</span>
            <span className="text-xs text-muted-foreground">· Registro longitudinal</span>
          </div>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Doses, sinais vitais, check-ins e evolução em um único ponto de entrada. Abra a tarefa clínica que precisa executar; os dados abaixo mostram somente registros disponíveis.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={() => setDoseOpen(true)} className="min-h-11 rounded-xl">
              <Activity className="mr-2 h-4 w-4" />Registrar dose ou exposição
            </Button>
            <Button asChild variant="outline" className="min-h-11 rounded-xl">
              <Link to="/sessoes?new=1"><Stethoscope className="mr-2 h-4 w-4" />Nova sessão</Link>
            </Button>
            <Button asChild variant="ghost" className="min-h-11 rounded-xl">
              <Link to="/pacientes"><NotebookPen className="mr-2 h-4 w-4" />Abrir prontuário</Link>
            </Button>
          </div>
        </div>
        <div className="relative z-10 mt-1 w-full shrink-0 rounded-2xl border bg-background/75 p-4 sm:max-w-[260px]">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock3 className="h-4 w-4" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em]">Janela de dados</span>
          </div>
          <div className="mt-3 text-sm font-semibold tabular-nums">{windowStartLabel}</div>
          <div className="my-1 h-px bg-border" />
          <div className="text-sm font-semibold tabular-nums">{windowEndLabel}</div>
          <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
            Período do painel. Registros fora desta janela não entram nos indicadores abaixo.
          </p>
        </div>
      </section>

      <section aria-label="Indicadores recentes" className="grid grid-cols-2 gap-2.5 md:grid-cols-3 xl:grid-cols-5">
        <Metric label="FREQUÊNCIA CARDÍACA" value={latestVital?.heart_rate_bpm} unit="bpm" sub={time(latestVital?.measured_at)} icon={HeartPulse} />
        <Metric label="PRESSÃO ARTERIAL" value={latestVital?.systolic_mm_hg != null && latestVital?.diastolic_mm_hg != null ? `${latestVital.systolic_mm_hg}/${latestVital.diastolic_mm_hg}` : null} unit="mmHg" sub={time(latestVital?.measured_at)} icon={Stethoscope} />
        <Metric label="SATURAÇÃO" value={latestVital?.spo2_percent} unit="%" sub={time(latestVital?.measured_at)} icon={Activity} />
        <Metric label="DOSES REGISTRADAS" value={doses.length} unit="reg." sub={`${uniqueSubstances} substância(s) distintas`} icon={Pill} />
        <Metric label="CHECK-INS" value={checkins.length} unit="reg." sub={time(latestCheckin?.checkin_at)} icon={BrainCircuit} />
      </section>

      <section className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(300px,.75fr)]">
        <Card className="clinical-panel min-w-0 overflow-hidden">
          <div className="clinical-panel-head">
            <div className="min-w-0">
              <div className="clinical-kicker">LINHA DO TEMPO</div>
              <h2 className="mt-1 font-semibold">Registros recentes</h2>
              <p className="mt-1 text-xs text-muted-foreground">Eventos documentados por horário real, quando disponível.</p>
            </div>
            <Button asChild size="sm" variant="outline" className="shrink-0 rounded-xl">
              <Link to="/journal">Abrir timeline <ArrowRight className="ml-1 h-4 w-4" /></Link>
            </Button>
          </div>
          <div className="divide-y">
            {recent.length ? recent.map((row: any) => (
              <div key={row.id} className="grid grid-cols-[76px_minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 sm:grid-cols-[96px_minmax(0,1fr)_auto]">
                <div className="font-mono text-[11px] tabular-nums text-muted-foreground">{time(row.actual_time)}</div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">{row.substance_name || "Exposição sem substância identificada"}</div>
                  <div className="mt-0.5 break-words text-[11px] leading-relaxed text-muted-foreground">
                    {row.dose_text || [row.dose_amount, row.dose_unit].filter(Boolean).join(" ") || "Dose não informada"} · {row.route || "via não informada"}
                  </div>
                </div>
                <span className="h-2 w-2 rounded-full bg-primary/80" aria-label="Registro documentado" />
              </div>
            )) : (
              <div className="p-6 sm:p-8">
                <div className="text-sm font-medium">Nenhuma dose encontrada nesta janela</div>
                <p className="mt-1 max-w-md text-sm leading-relaxed text-muted-foreground">Isso indica apenas ausência de registros retornados para o período selecionado — não confirma ausência de exposição.</p>
                <Button onClick={() => setDoseOpen(true)} variant="outline" className="mt-4 rounded-xl"><Activity className="mr-2 h-4 w-4" />Adicionar registro</Button>
              </div>
            )}
          </div>
        </Card>

        <div className="grid min-w-0 content-start gap-4">
          <Card className="clinical-panel p-4">
            <div className="clinical-kicker">ACESSO POR TAREFA</div>
            <h2 className="mt-1 font-semibold">O que você precisa revisar?</h2>
            <div className="mt-4 grid gap-2">
              <Link to="/inteligencia-clinica" className="group flex min-h-14 items-center gap-3 rounded-xl border p-3 transition-colors hover:bg-muted/40">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><BrainCircuit className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1"><span className="block text-sm font-medium">Síntese clínica</span><span className="mt-0.5 block text-xs text-muted-foreground">Dados, hipóteses e contexto</span></span>
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </Link>
              <Link to="/interacoes" className="group flex min-h-14 items-center gap-3 rounded-xl border p-3 transition-colors hover:bg-muted/40">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><ShieldAlert className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1"><span className="block text-sm font-medium">Interações e riscos</span><span className="mt-0.5 block text-xs text-muted-foreground">Combinações a conferir</span></span>
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </Link>
              <Link to="/meu-espaco" className="group flex min-h-14 items-center gap-3 rounded-xl border p-3 transition-colors hover:bg-muted/40">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><HeartPulse className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1"><span className="block text-sm font-medium">Sinais vitais e check-in</span><span className="mt-0.5 block text-xs text-muted-foreground">Estado atual e medições</span></span>
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </Link>
              <Link to="/laboratorio-pkpd" className="group flex min-h-14 items-center gap-3 rounded-xl border p-3 transition-colors hover:bg-muted/40">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"><Radar className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1"><span className="block text-sm font-medium">Laboratório PK/PD</span><span className="mt-0.5 block text-xs text-muted-foreground">Curvas, parâmetros e contexto</span></span>
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </Link>
            </div>
          </Card>
          <Card className="clinical-panel p-4">
            <div className="flex items-center gap-2"><Clock3 className="h-4 w-4 text-primary" /><div className="clinical-kicker">COBERTURA DO RECORTE</div></div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <div className="rounded-xl bg-muted/40 p-3"><div className="text-xl font-semibold tabular-nums">{doses.length}</div><div className="mt-1 text-[10px] leading-snug text-muted-foreground">doses/exposições</div></div>
              <div className="rounded-xl bg-muted/40 p-3"><div className="text-xl font-semibold tabular-nums">{vitals.length}</div><div className="mt-1 text-[10px] leading-snug text-muted-foreground">medições vitais</div></div>
              <div className="rounded-xl bg-muted/40 p-3"><div className="text-xl font-semibold tabular-nums">{checkins.length}</div><div className="mt-1 text-[10px] leading-snug text-muted-foreground">check-ins</div></div>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">Os totais descrevem o banco consultado nesta janela. A falta de registro não deve ser interpretada como evento negativo ou estabilidade clínica.</p>
          </Card>
        </div>
      </section>

      <section className="grid gap-3 md:grid-cols-3" aria-label="Áreas do ClinicalOS">
        <Card className="rounded-2xl p-4">
          <div className="flex items-center gap-2 text-primary"><NotebookPen className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">Prontuário</span></div>
          <h2 className="mt-2 font-semibold">Atendimento e evolução</h2>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">Consulte o histórico, documente a sessão e acompanhe sintomas-alvo.</p>
          <div className="mt-3 flex flex-wrap gap-2"><Button asChild size="sm" variant="outline" className="rounded-lg"><Link to="/sessoes">Sessões</Link></Button><Button asChild size="sm" variant="outline" className="rounded-lg"><Link to="/diario-paciente">Diário clínico</Link></Button><Button asChild size="sm" variant="outline" className="rounded-lg"><Link to="/sintomas">Sintomas</Link></Button></div>
        </Card>
        <Card className="rounded-2xl p-4">
          <div className="flex items-center gap-2 text-primary"><Pill className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">Tratamento</span></div>
          <h2 className="mt-2 font-semibold">Farmacologia e segurança</h2>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">Revise o esquema atual, registros de exposição e potenciais interações.</p>
          <div className="mt-3 flex flex-wrap gap-2"><Button asChild size="sm" variant="outline" className="rounded-lg"><Link to="/medicamentos">Tratamento</Link></Button><Button asChild size="sm" variant="outline" className="rounded-lg"><Link to="/uso-substancias">Uso de substâncias</Link></Button><Button asChild size="sm" variant="outline" className="rounded-lg"><Link to="/interacoes">Interações</Link></Button></div>
        </Card>
        <Card className="rounded-2xl p-4">
          <div className="flex items-center gap-2 text-primary"><Radar className="h-4 w-4" /><span className="text-xs font-semibold uppercase tracking-wide">Análise</span></div>
          <h2 className="mt-2 font-semibold">Modelos e indicadores</h2>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">Explore curvas temporais, PK/PD e tendências sem confundir estimativas com medições.</p>
          <div className="mt-3 flex flex-wrap gap-2"><Button asChild size="sm" variant="outline" className="rounded-lg"><Link to="/curvas">Curvas</Link></Button><Button asChild size="sm" variant="outline" className="rounded-lg"><Link to="/insights">Insights</Link></Button><Button asChild size="sm" variant="outline" className="rounded-lg"><Link to="/ferramentas">Todas as ferramentas</Link></Button></div>
        </Card>
      </section>

      <QuickIngestionWizard open={doseOpen} onOpenChange={setDoseOpen} patientId={PRIMARY_PATIENT_ID} />
    </div>
  );
}
